import { useId } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * Pennellata: il segno di marca, ripreso dal logo (assets/logo/vde-full.png).
 * Non e' un tratto calligrafico ma un blocco largo a bordi sfrangiati con
 * striature da pennello asciutto. Le striature sono una maschera a righe: dove
 * la maschera e' scura il colore non passa, ed e' quello che produce il
 * drybrush. Zero byte: nessun asset, nessuna richiesta di rete.
 */
export const Brush = ({
  className = '',
  color = 'var(--moka, #D6CDC0)',
  opacity = 1,
  animata = false,
  // MOTION-13: per una pennellata piu' giu' nella pagina, che altrimenti
  // finirebbe l'animazione prima ancora che l'utente ci arrivi con lo scroll.
  // Va usata solo quando l'elemento NON e' dentro un contenitore
  // overflow:hidden (vedi nota sotto su `animate`).
  soglia = false,
}) => {
  /**
   * useId perche' il componente compare piu' volte nella stessa pagina: due
   * <pattern> con lo stesso id si sovrascrivono e la seconda pennellata
   * ereditera' la maschera della prima.
   */
  const uid = useId().replace(/:/g, '');
  // clipPath non è una transform: reducedMotion="user" di MotionConfig non lo
  // tocca, e la pennellata continuerebbe ad animarsi anche con "riduci
  // animazioni" attivo. Con la preferenza attiva si salta al disegno finito.
  const ridotta = useReducedMotion();

  /**
   * Il disegno e' in `fill`, non in `stroke`: `pathLength` non e' applicabile.
   * Si ritaglia da sinistra a destra, che e' il gesto del pennello che si stende.
   * `animate` e non `whileInView`: dentro un contenitore con overflow:hidden
   * l'elemento non interseca mai e resterebbe invisibile per sempre.
   */
  const moto = animata && !ridotta
    ? {
        initial: { clipPath: 'inset(0 100% 0 0)' },
        transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
        ...(soglia
          ? { whileInView: { clipPath: 'inset(0 0% 0 0)' }, viewport: { once: true, margin: '-10% 0px' } }
          : { animate: { clipPath: 'inset(0 0% 0 0)' } }),
      }
    : {};

  return (
    <motion.svg
      className={className}
      viewBox="0 0 600 140"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
      {...moto}
    >
      <defs>
        <pattern id={`setole-${uid}`} width="600" height="15" patternUnits="userSpaceOnUse">
          <rect width="600" height="15" fill="#fff" />
          <rect y="3" width="600" height="1.7" fill="#000" opacity="0.5" />
          <rect y="8" width="430" height="1.2" fill="#000" opacity="0.34" />
          <rect x="170" y="11.5" width="360" height="1" fill="#000" opacity="0.26" />
        </pattern>
        <mask id={`drybrush-${uid}`}>
          <rect width="600" height="140" fill={`url(#setole-${uid})`} />
        </mask>
      </defs>
      <path
        mask={`url(#drybrush-${uid})`}
        fill={color}
        opacity={opacity}
        d="M6 22C90 14 180 12 270 16C360 20 450 14 540 18C560 19 580 20 594 24L594 118C570 124 540 126 510 124C420 118 330 122 240 126C160 129 90 126 20 118C14 117 8 116 6 112Z"
      />
    </motion.svg>
  );
};

export const MonogramPattern = ({ className = '', opacity = 0.07 }) => (
  <div
    className={className}
    aria-hidden="true"
    style={{
      opacity,
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='150' height='110' viewBox='0 0 150 110'><text x='6' y='40' font-family='Bodoni Moda, Didot, serif' font-size='34' fill='%23221D1A'>VdE</text><text x='78' y='90' font-family='Bodoni Moda, Didot, serif' font-size='34' fill='%23221D1A'>VdE</text></svg>\")",
      backgroundRepeat: 'repeat',
    }}
  />
);
